import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  TextField,
} from "@mui/material";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import "./FilterModal.css";

const FilterModal = ({ open, handleClose, onApplyFilter }) => {
  const [selectedFilter, setSelectedFilter] = useState("Date Range");
  const [selectedDateRange, setSelectedDateRange] = useState("Last 2 Month");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const filterCategories = [
    { id: "dateRange", label: "Date Range" },
    // Add more filter categories here if needed
  ];

  const dateRangeOptions = [
    { id: "today", label: "Today" },
    { id: "thisWeek", label: "This week" },
    { id: "thisMonth", label: "This month" },
    { id: "last2Month", label: "Last 2 Month" },
    { id: "custom", label: "Custom Range" },
  ];

  const handleClear = () => {
    setSelectedDateRange("Last 2 Month");
    setShowCustomRange(false);
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const getDateRange = (rangeType) => {
    const today = new Date();
    let startDate, endDate;

    switch (rangeType) {
      case "today":
        startDate = today.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
        break;

      case "thisWeek":
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        startDate = firstDayOfWeek.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
        break;

      case "thisMonth":
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = firstDayOfMonth.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
        break;

      case "last2Month":
        const twoMonthsAgo = new Date(today);
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        startDate = twoMonthsAgo.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
        break;

      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;

      default:
        const defaultTwoMonthsAgo = new Date(today);
        defaultTwoMonthsAgo.setMonth(defaultTwoMonthsAgo.getMonth() - 2);
        startDate = defaultTwoMonthsAgo.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
    }

    return { startDate, endDate };
  };

  const handleApply = () => {
    const dateRange = getDateRange(selectedDateRange);
    onApplyFilter(dateRange, selectedDateRange);
    handleClose();
  };

  const handleDateRangeChange = (value) => {
    setSelectedDateRange(value);
    if (value === "custom") {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          minHeight: "400px",
        },
      }}
    >
      <DialogContent sx={{ padding: 0, display: "flex", minHeight: "400px" }}>
        {/* Left Sidebar - Filter Categories */}
        <Box
          sx={{
            width: "200px",
            backgroundColor: "#f5f5f5",
            padding: "20px",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, marginBottom: "16px", fontSize: "14px" }}
          >
            Filters
          </Typography>
          {filterCategories.map((category) => (
            <Box
              key={category.id}
              onClick={() => setSelectedFilter(category.label)}
              sx={{
                padding: "10px 12px",
                marginBottom: "8px",
                cursor: "pointer",
                backgroundColor:
                  selectedFilter === category.label ? "#ffffff" : "transparent",
                borderLeft:
                  selectedFilter === category.label ? "3px solid #1976d2" : "3px solid transparent",
                borderRadius: "4px",
                fontWeight: selectedFilter === category.label ? 600 : 400,
                fontSize: "14px",
                color: selectedFilter === category.label ? "#1976d2" : "#333",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "#ffffff",
                },
              }}
            >
              {category.label}
            </Box>
          ))}
        </Box>

        {/* Right Section - Filter Options */}
        <Box sx={{ flex: 1, padding: "20px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, marginBottom: "20px", fontSize: "16px" }}
          >
            Date Range
          </Typography>

          {!showCustomRange ? (
            <RadioGroup value={selectedDateRange} onChange={(e) => handleDateRangeChange(e.target.value)}>
              {dateRangeOptions.map((option) => (
                <Box
                  key={option.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                    "&:hover": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  <FormControlLabel
                    value={option.id}
                    control={
                      <Radio
                        size="small"
                        sx={{
                          color: "#d0d0d0",
                          "&.Mui-checked": {
                            color: "#1976d2",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "14px", color: "#333" }}>
                        {option.label}
                      </Typography>
                    }
                    sx={{ margin: 0 }}
                  />
                  {option.id === "custom" && (
                    <ChevronRightIcon
                      style={{ width: 16, height: 16, color: "#999" }}
                    />
                  )}
                </Box>
              ))}
            </RadioGroup>
          ) : (
            <Box>
              <Button
                onClick={() => setShowCustomRange(false)}
                sx={{
                  marginBottom: "20px",
                  textTransform: "none",
                  color: "#1976d2",
                  fontSize: "14px",
                }}
              >
                ← Back to Date Range
              </Button>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  inputProps={{
                    min: customStartDate,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions
        sx={{
          padding: "16px 20px",
          borderTop: "1px solid #e0e0e0",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleClear}
          sx={{
            textTransform: "none",
            color: "#999",
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Clear
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: "#1976d2",
            fontSize: "14px",
            padding: "6px 24px",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;